<div class="phone-auth-container">
  <!-- Invisible reCAPTCHA container -->
  <div id="recaptcha-container" style="position: absolute; visibility: hidden;"></div>

  <!-- Background -->
  <div class="auth-background"></div>

  <!-- Main Card -->
  <div class="auth-card">

    <!-- Header -->
    <div class="auth-header">
      <div class="logo-section">
        <img src="/assets/befret_logo.png" alt="Befret" class="auth-logo">
        <h1 class="auth-title">
          {{ isOtpStep ? ('AUTH.PHONE.VERIFY_TITLE' | productionTranslate) : ('AUTH.PHONE.LOGIN_TITLE' | productionTranslate) }}
        </h1>
      </div>
      <p class="auth-subtitle">
        {{ isOtpStep ? ('AUTH.PHONE.VERIFY_SUBTITLE' | productionTranslate) : ('AUTH.PHONE.LOGIN_SUBTITLE' | productionTranslate) }}
      </p>
    </div>

    <!-- Back Button -->
    <button
      type="button"
      class="back-btn"
      (click)="isOtpStep ? onBackToPhone() : onBackToEmail()"
      [disabled]="state?.isLoading"
      [attr.aria-label]="'AUTH.PHONE.BACK' | productionTranslate">
      <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
        <path d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z"/>
      </svg>
      <span>{{ isOtpStep ? ('AUTH.PHONE.CHANGE_NUMBER' | productionTranslate) : ('AUTH.PHONE.BACK_TO_EMAIL' | productionTranslate) }}</span>
    </button>

    <!-- ============================================ -->
    <!-- STEP 1: Phone Number Input -->
    <!-- ============================================ -->
    <div class="step-container" *ngIf="isPhoneStep">
      <form [formGroup]="phoneForm" (ngSubmit)="onSendCode()">

        <!-- Phone Number Field -->
        <div class="form-group">
          <label class="form-label">
            {{ 'AUTH.PHONE.PHONE_NUMBER' | productionTranslate }}
            <span class="required">*</span>
          </label>

          <div class="phone-input-group">
            <!-- Country Prefix Select -->
            <select
              formControlName="phonePrefix"
              class="phone-prefix-select"
              [attr.aria-label]="'AUTH.PHONE.COUNTRY_CODE' | productionTranslate">
              <option *ngFor="let prefix of phonePrefixes" [value]="prefix.value">
                {{ prefix.short }}
              </option>
            </select>

            <!-- Phone Number Input -->
            <input
              type="tel"
              formControlName="phoneNumber"
              class="form-input phone-number-input"
              [class.error]="phoneForm.get('phoneNumber')?.invalid && phoneForm.get('phoneNumber')?.touched"
              [placeholder]="'AUTH.PHONE.PHONE_PLACEHOLDER' | productionTranslate"
              autocomplete="tel-national"
              inputmode="numeric"
              pattern="[0-9]*">
          </div>

          <!-- Validation Error -->
          <div class="field-error" *ngIf="phoneForm.get('phoneNumber')?.invalid && phoneForm.get('phoneNumber')?.touched">
            <span *ngIf="phoneForm.get('phoneNumber')?.errors?.['required']">
              {{ 'AUTH.PHONE.ERRORS.PHONE_REQUIRED' | productionTranslate }}
            </span>
            <span *ngIf="phoneForm.get('phoneNumber')?.errors?.['minlength']">
              {{ 'AUTH.PHONE.ERRORS.PHONE_TOO_SHORT' | productionTranslate }}
            </span>
            <span *ngIf="phoneForm.get('phoneNumber')?.errors?.['pattern']">
              {{ 'AUTH.PHONE.ERRORS.PHONE_DIGITS_ONLY' | productionTranslate }}
            </span>
          </div>

          <!-- Hint -->
          <p class="field-hint">
            {{ 'AUTH.PHONE.SMS_WILL_BE_SENT' | productionTranslate }}
          </p>
        </div>

        <!-- Error Alert -->
        <div class="auth-error" *ngIf="state?.error && isPhoneStep">
          <svg class="error-icon" viewBox="0 0 24 24">
            <path d="M1 21h22L12 2 1 21zm12-3h-2v-2h2v2zm0-4h-2v-4h2v4z"/>
          </svg>
          <span>{{ errorTranslationKey | productionTranslate }}</span>
        </div>

        <!-- Attempt Counter -->
        <div class="attempt-info" *ngIf="state && state.attemptCount && state.attemptCount > 0">
          {{ 'AUTH.PHONE.ATTEMPTS_REMAINING' | productionTranslate }}: {{ state.maxAttempts - state.attemptCount }}
        </div>

        <!-- Submit Button -->
        <button
          type="submit"
          class="auth-submit-btn"
          [disabled]="phoneForm.invalid || state?.isLoading">
          <span class="btn-text" *ngIf="!state?.isLoading">
            {{ 'AUTH.PHONE.SEND_CODE' | productionTranslate }}
          </span>
          <div class="btn-loader" *ngIf="state?.isLoading">
            <div class="spinner"></div>
            <span>{{ 'AUTH.PHONE.SENDING' | productionTranslate }}</span>
          </div>
        </button>

      </form>
    </div>

    <!-- ============================================ -->
    <!-- STEP 1.5: Account Check - "Do you have an account?" -->
    <!-- ============================================ -->
    <div class="step-container" *ngIf="isAccountCheckStep">
      <div class="account-check-card">
        <h2 class="account-check-title">
          {{ 'AUTH.PHONE.ACCOUNT_CHECK.TITLE' | productionTranslate }}
        </h2>
        <p class="account-check-subtitle">
          {{ 'AUTH.PHONE.ACCOUNT_CHECK.SUBTITLE' | productionTranslate }}
        </p>

        <div class="account-check-buttons">
          <button
            type="button"
            class="btn btn-primary btn-large"
            (click)="onUserHasAccount()"
            [disabled]="state?.isLoading">
            {{ 'AUTH.PHONE.ACCOUNT_CHECK.YES_I_HAVE' | productionTranslate }}
          </button>

          <button
            type="button"
            class="btn btn-secondary btn-large"
            (click)="onUserNewAccount()"
            [disabled]="state?.isLoading">
            {{ 'AUTH.PHONE.ACCOUNT_CHECK.NO_NEW_ACCOUNT' | productionTranslate }}
          </button>
        </div>

        <p class="account-check-info">
          {{ 'AUTH.PHONE.ACCOUNT_CHECK.INFO' | productionTranslate }}
        </p>
      </div>
    </div>

    <!-- ============================================ -->
    <!-- STEP 1.75: Email Link - Enter email to link account -->
    <!-- ============================================ -->
    <div class="step-container" *ngIf="isEmailLinkStep">
      <form [formGroup]="emailForm" (ngSubmit)="onSubmitEmail()">
        <div class="email-link-card">
          <h2 class="email-link-title">
            {{ 'AUTH.PHONE.EMAIL_LINK.TITLE' | productionTranslate }}
          </h2>
          <p class="email-link-subtitle">
            {{ 'AUTH.PHONE.EMAIL_LINK.SUBTITLE' | productionTranslate }}
          </p>

          <!-- Email Input -->
          <div class="form-group">
            <label class="form-label">
              {{ 'AUTH.FIELDS.EMAIL' | productionTranslate }}
              <span class="required">*</span>
            </label>
            <input
              type="email"
              formControlName="email"
              class="form-input"
              [class.error]="emailForm.get('email')?.invalid && emailForm.get('email')?.touched"
              [placeholder]="'AUTH.PLACEHOLDERS.EMAIL' | productionTranslate"
              autocomplete="email">

            <!-- Validation Error -->
            <div class="field-error" *ngIf="emailForm.get('email')?.invalid && emailForm.get('email')?.touched">
              <span *ngIf="emailForm.get('email')?.errors?.['required']">
                {{ 'AUTH.MESSAGES.VALIDATION.EMAIL_REQUIRED' | productionTranslate }}
              </span>
              <span *ngIf="emailForm.get('email')?.errors?.['email']">
                {{ 'AUTH.MESSAGES.VALIDATION.VALID_EMAIL_REQUIRED' | productionTranslate }}
              </span>
            </div>
          </div>

          <!-- Error Alert -->
          <div class="auth-error" *ngIf="state?.error && isEmailLinkStep && state?.error?.code !== 'use-google-auth'">
            <svg class="error-icon" viewBox="0 0 24 24">
              <path d="M1 21h22L12 2 1 21zm12-3h-2v-2h2v2zm0-4h-2v-4h2v4z"/>
            </svg>
            <span>{{ errorTranslationKey | productionTranslate }}</span>
          </div>

          <!-- Google Auth Required - Special handling -->
          <div class="google-auth-required" *ngIf="state?.error?.code === 'use-google-auth' && isEmailLinkStep">
            <div class="info-box">
              <svg class="info-icon" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-6h2v6zm0-8h-2V7h2v2z"/>
              </svg>
              <span>{{ 'AUTH.PHONE.ERRORS.USE_GOOGLE_AUTH' | productionTranslate }}</span>
            </div>
            <button
              type="button"
              class="google-signin-btn"
              (click)="onGoogleSignIn()"
              [disabled]="state?.isLoading">
              <svg class="google-icon" viewBox="0 0 24 24">
                <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
              </svg>
              <span>{{ 'AUTH.SOCIAL.CONTINUE_WITH_GOOGLE' | productionTranslate }}</span>
            </button>
          </div>

          <!-- Submit Button -->
          <button
            type="submit"
            class="auth-submit-btn"
            [disabled]="emailForm.invalid || state?.isLoading">
            <span *ngIf="!state?.isLoading">
              {{ 'AUTH.PHONE.EMAIL_LINK.CONTINUE' | productionTranslate }}
            </span>
            <span *ngIf="state?.isLoading">
              {{ 'AUTH.PHONE.EMAIL_LINK.SEARCHING' | productionTranslate }}
            </span>
          </button>

          <!-- Back to account check -->
          <button
            type="button"
            class="btn-text-link"
            (click)="onBackToAccountCheck()"
            [disabled]="state?.isLoading">
            {{ 'AUTH.PHONE.EMAIL_LINK.BACK' | productionTranslate }}
          </button>
        </div>
      </form>
    </div>

    <!-- ============================================ -->
    <!-- PASSWORD INPUT STEP (for MODE LINK) -->
    <!-- ============================================ -->
    <div class="step-container" *ngIf="isPasswordInputStep">
      <form [formGroup]="passwordForm" (ngSubmit)="onSubmitPassword()">
        <div class="password-card">
          <h2 class="password-title">
            {{ 'AUTH.PHONE.PASSWORD.TITLE' | productionTranslate }}
          </h2>
          <p class="password-subtitle">
            {{ 'AUTH.PHONE.PASSWORD.SUBTITLE' | productionTranslate }}
            <strong>{{ state?.email }}</strong>
          </p>

          <!-- Password Input -->
          <div class="form-group">
            <label class="form-label">
              {{ 'AUTH.FIELDS.PASSWORD' | productionTranslate }}
              <span class="required">*</span>
            </label>
            <div class="password-input-wrapper">
              <input
                [type]="showPassword ? 'text' : 'password'"
                formControlName="password"
                class="form-input"
                [class.error]="passwordForm.get('password')?.invalid && passwordForm.get('password')?.touched"
                [placeholder]="'AUTH.PLACEHOLDERS.PASSWORD' | productionTranslate"
                autocomplete="current-password">

              <!-- Show/Hide Password Toggle -->
              <button
                type="button"
                class="password-toggle-btn"
                (click)="showPassword = !showPassword"
                tabindex="-1">
                <svg *ngIf="!showPassword" class="toggle-icon" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z"/>
                </svg>
                <svg *ngIf="showPassword" class="toggle-icon" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 7c2.76 0 5 2.24 5 5 0 .65-.13 1.26-.36 1.83l2.92 2.92c1.51-1.26 2.7-2.89 3.43-4.75-1.73-4.39-6-7.5-11-7.5-1.4 0-2.74.25-3.98.7l2.16 2.16C10.74 7.13 11.35 7 12 7zM2 4.27l2.28 2.28.46.46C3.08 8.3 1.78 10.02 1 12c1.73 4.39 6 7.5 11 7.5 1.55 0 3.03-.3 4.38-.84l.42.42L19.73 22 21 20.73 3.27 3 2 4.27zM7.53 9.8l1.55 1.55c-.05.21-.08.43-.08.65 0 1.66 1.34 3 3 3 .22 0 .44-.03.65-.08l1.55 1.55c-.67.33-1.41.53-2.2.53-2.76 0-5-2.24-5-5 0-.79.2-1.53.53-2.2zm4.31-.78l3.15 3.15.02-.16c0-1.66-1.34-3-3-3l-.17.01z"/>
                </svg>
              </button>
            </div>

            <!-- Validation Error -->
            <div class="field-error" *ngIf="passwordForm.get('password')?.invalid && passwordForm.get('password')?.touched">
              <span *ngIf="passwordForm.get('password')?.errors?.['required']">
                {{ 'AUTH.MESSAGES.VALIDATION.PASSWORD_REQUIRED' | productionTranslate }}
              </span>
              <span *ngIf="passwordForm.get('password')?.errors?.['minlength']">
                {{ 'AUTH.MESSAGES.VALIDATION.PASSWORD_MIN_LENGTH' | productionTranslate }}
              </span>
            </div>
          </div>

          <!-- Error Alert -->
          <div class="auth-error" *ngIf="state?.error && isPasswordInputStep">
            <svg class="error-icon" viewBox="0 0 24 24">
              <path d="M1 21h22L12 2 1 21zm12-3h-2v-2h2v2zm0-4h-2v-4h2v4z"/>
            </svg>
            <span>{{ errorTranslationKey | productionTranslate }}</span>
          </div>

          <!-- Submit Button -->
          <button
            type="submit"
            class="auth-submit-btn"
            [disabled]="passwordForm.invalid || state?.isLoading">
            <span *ngIf="!state?.isLoading">
              {{ 'AUTH.PHONE.PASSWORD.CONTINUE' | productionTranslate }}
            </span>
            <span *ngIf="state?.isLoading">
              {{ 'AUTH.PHONE.PASSWORD.AUTHENTICATING' | productionTranslate }}
            </span>
          </button>

          <!-- Back to email input -->
          <button
            type="button"
            class="btn-text-link"
            (click)="onBackToEmailLink()"
            [disabled]="state?.isLoading">
            {{ 'AUTH.PHONE.PASSWORD.BACK' | productionTranslate }}
          </button>
        </div>
      </form>
    </div>

    <!-- ============================================ -->
    <!-- EXISTING PROVIDER LOGIN STEP -->
    <!-- Phone found in profile, but user needs to login with Google/Email first -->
    <!-- ============================================ -->
    <div class="step-container" *ngIf="isExistingProviderLoginStep">
      <div class="existing-provider-card">
        <div class="info-icon-wrapper">
          <svg class="info-icon-large" viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-6h2v6zm0-8h-2V7h2v2z"/>
          </svg>
        </div>

        <h2 class="existing-provider-title">
          {{ 'AUTH.PHONE.EXISTING_PROVIDER.TITLE' | productionTranslate }}
        </h2>

        <p class="existing-provider-subtitle">
          {{ 'AUTH.PHONE.EXISTING_PROVIDER.SUBTITLE' | productionTranslate }}
          <strong>{{ state?.linkState?.existingProfile?.email }}</strong>
        </p>

        <p class="existing-provider-info">
          {{ 'AUTH.PHONE.EXISTING_PROVIDER.INFO' | productionTranslate }}
        </p>

        <!-- Error Alert -->
        <div class="auth-error" *ngIf="state?.error && isExistingProviderLoginStep">
          <svg class="error-icon" viewBox="0 0 24 24">
            <path d="M1 21h22L12 2 1 21zm12-3h-2v-2h2v2zm0-4h-2v-4h2v4z"/>
          </svg>
          <span>{{ errorTranslationKey | productionTranslate }}</span>
        </div>

        <!-- Google Login Button -->
        <button
          *ngIf="state?.linkState?.existingProvider === 'google'"
          type="button"
          class="google-signin-btn large"
          (click)="onLoginWithExistingProvider()"
          [disabled]="state?.isLoading">
          <svg class="google-icon" viewBox="0 0 24 24">
            <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
            <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
            <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
            <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
          </svg>
          <span *ngIf="!state?.isLoading">{{ 'AUTH.PHONE.EXISTING_PROVIDER.CONTINUE_GOOGLE' | productionTranslate }}</span>
          <span *ngIf="state?.isLoading">{{ 'AUTH.PHONE.EXISTING_PROVIDER.CONNECTING' | productionTranslate }}</span>
        </button>

        <!-- Email/Password Login Button -->
        <button
          *ngIf="state?.linkState?.existingProvider === 'email'"
          type="button"
          class="auth-submit-btn"
          (click)="onLoginWithExistingProvider()"
          [disabled]="state?.isLoading">
          <span *ngIf="!state?.isLoading">{{ 'AUTH.PHONE.EXISTING_PROVIDER.CONTINUE_EMAIL' | productionTranslate }}</span>
          <span *ngIf="state?.isLoading">{{ 'AUTH.PHONE.EXISTING_PROVIDER.CONNECTING' | productionTranslate }}</span>
        </button>

        <!-- Back Button -->
        <button
          type="button"
          class="btn-text-link"
          (click)="onBackFromExistingProvider()"
          [disabled]="state?.isLoading">
          {{ 'AUTH.PHONE.EXISTING_PROVIDER.BACK' | productionTranslate }}
        </button>
      </div>
    </div>

    <!-- ============================================ -->
    <!-- STEP 2: OTP Verification -->
    <!-- ============================================ -->
    <div class="step-container" *ngIf="isOtpStep">

      <!-- Phone Number Display -->
      <div class="phone-display">
        <svg class="phone-icon" viewBox="0 0 24 24" fill="currentColor">
          <path d="M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z"/>
        </svg>
        <span>{{ state?.formattedPhoneNumber }}</span>
      </div>

      <!-- OTP Input Group -->
      <div class="form-group">
        <label class="form-label otp-label">
          {{ 'AUTH.PHONE.ENTER_CODE' | productionTranslate }}
        </label>

        <div class="otp-input-group" (paste)="onOtpPaste($event)">
          <input
            *ngFor="let digit of otpDigits; let i = index; trackBy: trackByIndex"
            #otpInput
            type="text"
            inputmode="numeric"
            pattern="[0-9]*"
            maxlength="2"
            class="otp-digit-input"
            [class.filled]="digit !== ''"
            [class.error]="state?.error?.code === 'auth/invalid-verification-code'"
            [value]="digit"
            (input)="onOtpInput($event, i)"
            (keydown)="onOtpKeydown($event, i)"
            [attr.aria-label]="('AUTH.PHONE.DIGIT' | productionTranslate) + ' ' + (i + 1)"
            autocomplete="one-time-code">
        </div>

        <!-- OTP Hint -->
        <p class="field-hint otp-hint">
          {{ 'AUTH.PHONE.CODE_SENT_TO' | productionTranslate }}
        </p>
      </div>

      <!-- Error Alert -->
      <div class="auth-error" *ngIf="state?.error && isOtpStep">
        <svg class="error-icon" viewBox="0 0 24 24">
          <path d="M1 21h22L12 2 1 21zm12-3h-2v-2h2v2zm0-4h-2v-4h2v4z"/>
        </svg>
        <span>{{ errorTranslationKey | productionTranslate }}</span>
      </div>

      <!-- Verify Button -->
      <button
        type="button"
        class="auth-submit-btn"
        [disabled]="!isOtpComplete || state?.isLoading"
        (click)="onVerifyCode()">
        <span class="btn-text" *ngIf="!state?.isLoading">
          {{ 'AUTH.PHONE.VERIFY_CODE' | productionTranslate }}
        </span>
        <div class="btn-loader" *ngIf="state?.isLoading">
          <div class="spinner"></div>
          <span>{{ 'AUTH.PHONE.VERIFYING' | productionTranslate }}</span>
        </div>
      </button>

      <!-- Resend Section -->
      <div class="resend-section">
        <p class="resend-text">{{ 'AUTH.PHONE.DIDNT_RECEIVE' | productionTranslate }}</p>

        <button
          type="button"
          class="resend-btn"
          [disabled]="!state?.canResend || state?.isLoading"
          (click)="onResendCode()">
          <span *ngIf="state?.canResend">
            {{ 'AUTH.PHONE.RESEND_CODE' | productionTranslate }}
          </span>
          <span *ngIf="!state?.canResend" class="cooldown-text">
            {{ 'AUTH.PHONE.RESEND_IN' | productionTranslate }} {{ formattedCooldown }}
          </span>
        </button>
      </div>

    </div>

    <!-- ============================================ -->
    <!-- SUCCESS STATE -->
    <!-- ============================================ -->
    <div class="step-container success-container" *ngIf="isSuccess">
      <div class="success-icon-wrapper">
        <svg class="success-checkmark" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path d="M20 6L9 17l-5-5"/>
        </svg>
      </div>
      <h2 class="success-title">{{ 'AUTH.PHONE.SUCCESS_TITLE' | productionTranslate }}</h2>
      <p class="success-message">{{ 'AUTH.PHONE.SUCCESS_MESSAGE' | productionTranslate }}</p>
      <div class="redirect-loader">
        <div class="spinner small"></div>
        <span>{{ 'AUTH.PHONE.REDIRECTING' | productionTranslate }}</span>
      </div>
    </div>

    <!-- Terms -->
    <div class="auth-terms" *ngIf="isPhoneStep">
      <p>
        {{ 'AUTH.MESSAGES.INFO.TERMS_INTRO' | productionTranslate }}
        <a routerLink="/conditions" target="_blank">{{ 'AUTH.LINKS.TERMS_OF_SERVICE' | productionTranslate }}</a>
        {{ 'AUTH.MESSAGES.INFO.AND_OUR' | productionTranslate }}
        <a routerLink="/privacy" target="_blank">{{ 'AUTH.LINKS.PRIVACY_POLICY' | productionTranslate }}</a>.
      </p>
    </div>

  </div>

  <!-- Loading overlay -->
  <div class="loading-overlay" *ngIf="state?.isLoading">
    <div class="loading-spinner"></div>
  </div>

</div>
